// frontend/src/components/admin/RecordingsTab.jsx
//
// 2026-10-08 — Super admin → Recordings. Every game narrated into the app
// (Game Day keeps them all), with the audio as one file and a debug bundle
// (audio + what the engine heard + the events Game Day wrote + the voice
// log) per recording, or one zip for several at once. For debugging the
// voice pipeline on real games; these hold real people's voices and names,
// so they stay in this chat / on your machine.
import { useEffect, useMemo, useState } from 'react';
import { Download, Mic, RefreshCw, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';

const fmtDur = (ms) => { const s = Math.round((ms || 0) / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const fmtMB = (b) => `${((b || 0) / 1048576).toFixed(1)} MB`;

// Authenticated download: the API needs the bearer token, so fetch the
// bytes with axios and hand them to the browser as a file.
async function downloadBlob(path, filename, opts = {}) {
  const res = await api.request({ url: path, method: opts.method || 'GET', data: opts.data, responseType: 'blob', timeout: 10 * 60 * 1000 });
  const cd = res.headers?.['content-disposition'] || '';
  const m = cd.match(/filename="([^"]+)"/);
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a'); a.href = url; a.download = (m && m[1]) || filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function RecordingsTab({ T }) {
  const [rows, setRows] = useState(null);
  const [days, setDays] = useState(60);
  const [sel, setSel] = useState(() => new Set());
  const [busy, setBusy] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let c = false;
    api.get('/admin/recordings', { params: { days } }).then(({ data }) => { if (!c) setRows(data.recordings || []); }).catch(() => { if (!c) setRows([]); });
    return () => { c = true; };
  }, [days, tick]);

  const teams = useMemo(() => [...new Set((rows || []).map(r => r.team_name))].sort(), [rows]);
  const [teamFilter, setTeamFilter] = useState('');
  const shown = (rows || []).filter(r => !teamFilter || r.team_name === teamFilter);

  const one = async (r, kind) => {
    setBusy(r.id + kind);
    try {
      if (kind === 'audio') await downloadBlob(`/admin/recordings/${r.id}/audio`, `recording.${r.mime === 'audio/webm' ? 'webm' : 'm4a'}`);
      else if (kind === 'json') await downloadBlob(`/admin/recordings/${r.id}/debug-json`, `pressbox_debug_${r.id.slice(0, 8)}.json`);
      else await downloadBlob(`/admin/recordings/${r.id}/bundle`, `recording_${r.id.slice(0, 8)}.zip`);
    } catch (e) { toast.error(e.response?.status === 404 ? (kind === 'json' ? 'Super admin only' : 'That recording has no audio') : 'Download failed'); }
    finally { setBusy(null); }
  };
  const bulk = async () => {
    if (!sel.size) return;
    setBusy('bulk');
    try { await downloadBlob('/admin/recordings/bundle', 'recordings.zip', { method: 'POST', data: { ids: [...sel] } }); toast.success(`Bundled ${sel.size} recording${sel.size === 1 ? '' : 's'}`); }
    catch { toast.error('Bundle failed'); }
    finally { setBusy(null); }
  };
  const toggleAll = () => setSel(s => s.size === shown.length ? new Set() : new Set(shown.map(r => r.id)));

  const cell = { padding: '6px 8px', fontSize: 12, borderBottom: `1px solid ${T.border}`, verticalAlign: 'top' };
  const head = { ...cell, fontSize: 10, fontWeight: 700, color: T.text2, textTransform: 'uppercase', letterSpacing: '0.06em' };
  const btn = { background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, padding: '3px 8px', color: T.text2, cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <Mic size={16} color={T.ice} />
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Voice recordings</h3>
        <span style={{ fontSize: 12, color: T.text2 }}>— every game narrated into the app. Kept for good; download for debugging.</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={{ ...btn, cursor: 'pointer' }}>
            {[7, 30, 60, 180, 365, 730].map(d => <option key={d} value={d}>last {d} days</option>)}
          </select>
          <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)} style={{ ...btn, cursor: 'pointer' }}>
            <option value="">all teams</option>{teams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button type="button" style={btn} onClick={() => setTick(t => t + 1)}><RefreshCw size={11} /> Refresh</button>
          <button type="button" onClick={bulk} disabled={!sel.size || busy === 'bulk'}
            style={{ ...btn, background: sel.size ? T.ice : 'transparent', color: sel.size ? '#000' : T.text2, fontWeight: 700 }}>
            <Package size={12} /> {busy === 'bulk' ? 'Bundling…' : `Download ${sel.size || ''} selected`}
          </button>
        </div>
      </div>
      <p style={{ fontSize: 11, color: T.text2, marginBottom: 10 }}>
        <strong>Audio</strong> = the recording as one file. <strong>Bundle</strong> = a zip with the audio, what the engine heard (transcript), the events
        Game Day wrote, and the voice log — everything needed to trace a missed goal. Tick several and "Download selected" for one zip with a folder per recording (max 50).
      </p>
      {rows === null ? <div style={{ fontSize: 12, color: T.text2 }}>Loading…</div> : shown.length === 0 ? <div style={{ fontSize: 12, color: T.text2 }}>No recordings in this window.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead><tr>
              <th style={head}><input type="checkbox" checked={sel.size === shown.length && shown.length > 0} onChange={toggleAll} /></th>
              <th style={head}>When</th><th style={head}>Team · game</th><th style={head}>Length</th><th style={head}>Audio</th>
              <th style={head}>Engine</th><th style={head}>Goals heard</th><th style={head}>Log</th><th style={head}></th>
            </tr></thead>
            <tbody>
              {shown.map(r => (
                <tr key={r.id} data-recording={r.id}>
                  <td style={cell}><input type="checkbox" checked={sel.has(r.id)} onChange={() => setSel(s => { const n = new Set(s); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })} /></td>
                  <td style={cell}>{new Date(r.started_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                  <td style={cell}><div style={{ fontWeight: 600, color: T.text }}>{r.team_name}</div>
                    <div style={{ color: T.text2 }}>{r.game_date ? new Date(r.game_date).toISOString().slice(0, 10) : '—'}{r.opponent_name ? ` vs ${r.opponent_name}` : ''}{r.parent_game_id ? ' (mini-game)' : ''}</div></td>
                  <td style={cell}>{fmtDur(r.duration_ms)}{r.gap_count ? <span style={{ color: T.puck }}> · {r.gap_count} gaps</span> : ''}</td>
                  <td style={cell}>{r.chunk_count} chunks · {fmtMB(r.total_bytes)}<div style={{ color: T.text2 }}>{r.status}{r.recorder_role === 'backup' ? ' · backup' : ''}</div></td>
                  <td style={{ ...cell, color: r.engine_error ? T.fire : T.text }}>{r.engine_status || 'off'}{r.engine_mode ? ` · ${r.engine_mode}` : ''}{r.engine_score ? ` · ${r.engine_score.us}–${r.engine_score.them}` : ''}{r.engine_error ? <div style={{ fontSize: 11 }}>{r.engine_error}</div> : null}</td>
                  <td style={cell}>{r.goals_heard}</td>
                  <td style={{ ...cell, color: r.log_errors ? T.fire : T.text2 }}>{r.log_rows ? `${r.log_rows} rows${r.log_errors ? `, ${r.log_errors} errors` : ''}` : '—'}</td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                    <button type="button" style={btn} disabled={!r.chunk_count || busy === r.id + 'audio'} onClick={() => one(r, 'audio')} title="The recording as one audio file"><Download size={11} /> Audio</button>{' '}
                    <button type="button" style={btn} disabled={busy === r.id + 'bundle'} onClick={() => one(r, 'bundle')} title="Zip: audio + transcript + events + voice log"><Package size={11} /> Bundle</button>{' '}
                    <button type="button" style={btn} disabled={busy === r.id + 'json'} onClick={() => one(r, 'json')} title="Troubleshooting JSON (super admin only): transcript, analysis, events, roster and log in one file"><Download size={11} /> JSON</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
